/**
 * One row per kind of suggestion, each with a checkbox saying whether it is kept: a
 * description for a node, a group (its parent, members and gathered groups), a review finding,
 * and after a scan a new node for code no plugin read, categories, another kind, a link.
 */
import { For, Show, type JSX } from "solid-js";
import type { AiFinding } from "@protocol/AiFinding";
import type { AiGroup } from "@protocol/AiGroup";
import type { AiCategories } from "@protocol/AiCategories";
import type { AiKind } from "@protocol/AiKind";
import type { AiLink } from "@protocol/AiLink";
import type { AiNode } from "@protocol/AiNode";
import type { AiNote } from "@protocol/AiNote";
import { useApp } from "../../app/context";
import { categoriesKey, findingKey, groupKey, kindKey, linkKey, nodeKey, noteKey } from "../../state/ai";
import { NodeChip } from "./NodeChip";
import { shortValues } from "../../shared/fields";

/** The "keep it" checkbox of suggestion `item` (see `suggestionKeys` in `state/ai.ts`). */
function Keep(props: { item: string }): JSX.Element {
  const { ai } = useApp();
  return <input type="checkbox" data-ai-keep={props.item} checked={ai.isKept(props.item)} onChange={(e) => ai.setKept(props.item, e.currentTarget.checked)} />;
}

/** A suggested description, next to the node's current note if it has one. */
export function NoteRow(props: { note: AiNote }): JSX.Element {
  return (
    <li class="ai-row" data-ai-note={props.note.node}>
      <Keep item={noteKey(props.note.node)} />
      <div>
        <NodeChip id={props.note.node} />
        <p class="small">{props.note.note}</p>
      </div>
    </li>
  );
}

/** A suggested group: name, where it goes, what it is for, its members. */
export function GroupRow(props: { group: AiGroup }): JSX.Element {
  const { graph } = useApp();
  /** The parent's name: an existing group's, a suggested one's id, or nothing. */
  const parent = () => {
    const id = props.group.parent;
    if (!id) return null;
    return graph.state.graph.groups.find((g) => g.id === id)?.name ?? id;
  };
  return (
    <li class="ai-row" data-ai-group={props.group.id}>
      <Keep item={groupKey(props.group.id)} />
      <div>
        <p>
          <strong>{props.group.name}</strong> <span class="muted small mono">{props.group.id}</span>
          <Show when={parent()}>
            <span class="muted small"> in {parent()}</span>
          </Show>
        </p>
        <Show when={props.group.note}>
          <p class="small">{props.group.note}</p>
        </Show>
        <p class="chips">
          <For each={props.group.members}>{(id) => <NodeChip id={id} />}</For>
        </p>
        <Show when={props.group.groups?.length}>
          <p class="small muted" data-ai-gathers={props.group.groups.join(" ")}>
            Gathers {props.group.groups.map((id) => graph.state.graph.groups.find((g) => g.id === id)?.name ?? id).join(", ")}
          </p>
        </Show>
      </div>
    </li>
  );
}

/** A node suggested for code no plugin read: its kind, name, file and description. */
export function NewNodeRow(props: { node: AiNode }): JSX.Element {
  const { kinds } = useApp();
  const n = () => props.node.node;
  return (
    <li class="ai-row" data-ai-new-node={n().id}>
      <Keep item={nodeKey(n().id)} />
      <div>
        <p>
          <span class="muted small">new {kinds.ofNode(n()).label.toLowerCase()}</span> <strong>{n().name}</strong>
          <For each={shortValues(n())}>{(value) => <span class="muted small"> · {value}</span>}</For>
        </p>
        <Show when={n().source}>
          <p class="small muted mono">{n().source}</p>
        </Show>
        <Show when={n().note}>
          <p class="small">{n().note}</p>
        </Show>
      </div>
    </li>
  );
}

/** Categories suggested for a node (added to the ones it has). */
export function CategoriesRow(props: { categories: AiCategories }): JSX.Element {
  return (
    <li class="ai-row" data-ai-categories={props.categories.node}>
      <Keep item={categoriesKey(props.categories.node)} />
      <div>
        <NodeChip id={props.categories.node} name={props.categories.name} />
        <p class="small">+ {props.categories.categories.join(", ")}</p>
      </div>
    </li>
  );
}

/** Another kind suggested for a node, and why. */
export function KindRow(props: { kind: AiKind }): JSX.Element {
  const { kinds } = useApp();
  const label = () => kinds.ofNode({ kind: props.kind.kind }).label;
  return (
    <li class="ai-row" data-ai-kind={props.kind.node}>
      <Keep item={kindKey(props.kind.node)} />
      <div>
        <p>
          <NodeChip id={props.kind.node} name={props.kind.name} /> <span class="small">→ {label()}</span>
        </p>
        <Show when={props.kind.why}>
          <p class="small muted">{props.kind.why}</p>
        </Show>
      </div>
    </li>
  );
}

/** A suggested link between two nodes, and why. */
export function LinkRow(props: { link: AiLink; names: (id: string) => string | undefined }): JSX.Element {
  const { kinds } = useApp();
  const verb = () => kinds.ofLink({ kind: props.link.kind }).verb;
  return (
    <li class="ai-row" data-ai-link={linkKey(props.link)}>
      <Keep item={linkKey(props.link)} />
      <div>
        <p>
          <NodeChip id={props.link.from} name={props.names(props.link.from)} /> <span class="small">{verb()}</span> <NodeChip id={props.link.to} name={props.names(props.link.to)} />
        </p>
        <Show when={props.link.why}>
          <p class="small muted">{props.link.why}</p>
        </Show>
      </div>
    </li>
  );
}

/** A review finding; kept, it becomes a comment on each of its nodes. */
export function FindingRow(props: { finding: AiFinding; index: number }): JSX.Element {
  const f = () => props.finding;
  return (
    <li class="ai-row" data-ai-finding={props.index} data-level={f().level}>
      <Show when={f().nodes.length} fallback={<span class="ai-nokeep" title="About no node: nothing to apply" />}>
        <Keep item={findingKey(props.index)} />
      </Show>
      <div>
        <p>
          <span class={`ai-level ${f().level}`}>{f().level}</span> <strong>{f().title}</strong>
        </p>
        <Show when={f().detail}>
          <p class="small">{f().detail}</p>
        </Show>
        <Show when={f().nodes.length}>
          <p class="chips">
            <For each={f().nodes}>{(id) => <NodeChip id={id} />}</For>
          </p>
        </Show>
      </div>
    </li>
  );
}
